import { isHttpSecure } from "lib/utils/utils";
import { joinUrl } from "lib/utils/connection";
import { gatewayServiceBaseUrl } from "lib/consts/arcGateway";
import { getAuthToken } from "lib/utils/auth";
import { authedFetch } from "lib/utils/authedFetch";
import { messageFromFailedResponse, parseFastApiDetail } from "lib/utils/userFacingApiError";

/** HTTP API for the recorder service (FastAPI + GStreamer), default port 12395. */

export type RecordingFileInfo = {
    name: string;
    path: string;
    size_bytes: number;
    modified_utc: string;
    /** From optional `{name}.meta.json` sidecar (may be absent on older servers). */
    remark?: string;
};

export type RecordingsListResponse = {
    directory: string;
    files: RecordingFileInfo[];
};

export type RecordingStatus = {
    active: boolean;
    pid: number | null;
    output_path: string | null;
    started_at_utc: string | null;
    error: string | null;
};

const DEFAULT_RECORDER_PORT = "12395";

/**
 * Browser `<img>` / `<video>` cannot set Authorization. arc_gateway `forward_auth`
 * accepts the ARC JWT as `?token=` (same as map previews / WebSockets).
 */
function withGatewayAuthToken(url: string): string {
    const token = getAuthToken();
    if (!token) return url;
    const u = new URL(url);
    u.searchParams.set("token", token);
    return u.toString();
}

/** Base URL with no trailing slash. Uses `selectedArcHost` when set (same pattern as WiFi API). */
export function getRecorderBaseUrl(selectedArcHost?: string | null, isSecure: boolean = isHttpSecure(), arcId?: string | null): string | null {
    if (process.env.REACT_APP_RECORDER_DISABLED === "true") {
        return null;
    }
    const explicit = process.env.REACT_APP_RECORDER_BASE_URL?.trim();
    if (explicit) {
        return explicit.replace(/\/$/, "");
    }
    const port = process.env.REACT_APP_RECORDER_PORT?.trim() || DEFAULT_RECORDER_PORT;
    const proxyHost =
        selectedArcHost?.trim() ||
        process.env.REACT_APP_RECORDER_HOST?.trim();
    const host =
        proxyHost ||
        process.env.REACT_APP_SINGLE_ARC_HOSTNAME?.trim() ||
        process.env.REACT_APP_HOST_OVERRIDE?.trim() ||
        (typeof window !== "undefined" ? window.location.hostname : "");
    if (!host) {
        return null;
    }
    const protocol = isSecure ? "https" : "http";
    // Direct mode uses arc_gateway; fleet mode uses the proxy's path-routed
    // origin. A fleet ARC without a proxy host is unavailable.
    const gatewayBase = gatewayServiceBaseUrl("recorder", arcId, proxyHost);
    if (gatewayBase) return gatewayBase;
    if (arcId) return null;

    return `${protocol}://${host}:${port}`;
}

/** URL for GET /recordings/file/{name} (used by `<img>` / `<video src>` previews). */
export function getRecordingFileUrl(baseUrl: string, filename: string): string {
    return withGatewayAuthToken(
        joinUrl(baseUrl, `/recordings/file/${encodeURIComponent(filename)}`)
    );
}

/** GET file as blob for client-side download (same endpoint as streaming). */
export async function fetchRecordingFileBlob(
    baseUrl: string,
    filename: string
): Promise<Blob> {
    const url = getRecordingFileUrl(baseUrl, filename);
    const res = await authedFetch(url, fetchOptsNoCache);
    if (!res.ok) {
        throw new Error(await parseError(res));
    }
    return res.blob();
}

async function parseError(res: Response): Promise<string> {
    let detail: string | undefined;
    try {
        const body = (await res.json()) as { detail?: unknown };
        detail = parseFastApiDetail(body);
    } catch {
        /* ignore */
    }
    return messageFromFailedResponse(
        res.status,
        detail,
        "Couldn't complete the recorder request. Try again."
    );
}

const fetchOptsNoCache: RequestInit = { cache: "no-store" };

export async function fetchRecordings(baseUrl: string): Promise<RecordingsListResponse> {
    const url = joinUrl(baseUrl, "/recordings");
    const res = await authedFetch(url, fetchOptsNoCache);
    if (!res.ok) {
        throw new Error(await parseError(res));
    }
    return res.json() as Promise<RecordingsListResponse>;
}

export async function fetchRecordStatus(baseUrl: string): Promise<RecordingStatus> {
    const url = joinUrl(baseUrl, "/record/status");
    const res = await authedFetch(url, fetchOptsNoCache);
    if (!res.ok) {
        throw new Error(await parseError(res));
    }
    return res.json() as Promise<RecordingStatus>;
}

type RecordActionResponse = {
    ok: boolean;
    message: string;
    status: RecordingStatus;
};

export async function postRecordStart(
    baseUrl: string,
    filename?: string | null
): Promise<RecordingStatus> {
    const u = new URL(joinUrl(baseUrl, "/record/start"));
    if (filename) u.searchParams.set("filename", filename);
    const res = await authedFetch(u.toString(), {
        method: "POST",
        ...fetchOptsNoCache,
    });
    if (!res.ok) {
        throw new Error(await parseError(res));
    }
    const data = (await res.json()) as RecordActionResponse;
    return data.status;
}

export async function postRecordStop(baseUrl: string): Promise<RecordingStatus> {
    const url = joinUrl(baseUrl, "/record/stop");
    const res = await authedFetch(url, { method: "POST", ...fetchOptsNoCache });
    if (!res.ok) {
        throw new Error(await parseError(res));
    }
    const data = (await res.json()) as RecordActionResponse;
    return data.status;
}

export type PhotoCaptureStartResponse = {
    accepted: boolean;
};

export type PhotoCaptureStatus = {
    busy: boolean;
    last_error: string | null;
    last_path: string | null;
    last_filename: string | null;
};

/** GPS payload stamped into `{photo}.meta.json` when the still is captured. */
export type PhotoCaptureGps = {
    latitude_deg: number;
    longitude_deg: number;
    altitude_m?: number;
    fix?: number;
    satellites?: number;
};

/** POST /photo/capture — queues a JPEG still; returns immediately. Poll with photo capture status / waitForPhotoCapture. */
export async function postPhotoCapture(
    baseUrl: string,
    filename?: string | null,
    gps?: PhotoCaptureGps | null
): Promise<PhotoCaptureStartResponse> {
    const u = new URL(joinUrl(baseUrl, "/photo/capture"));
    if (filename) u.searchParams.set("filename", filename);
    const init: RequestInit = {
        method: "POST",
        ...fetchOptsNoCache,
    };
    if (gps) {
        init.headers = { "Content-Type": "application/json" };
        init.body = JSON.stringify({ gps });
    }
    const res = await authedFetch(u.toString(), init);
    if (!res.ok) {
        throw new Error(await parseError(res));
    }
    return res.json() as Promise<PhotoCaptureStartResponse>;
}

export async function fetchPhotoCaptureStatus(baseUrl: string): Promise<PhotoCaptureStatus> {
    const url = joinUrl(baseUrl, "/photo/capture/status");
    const res = await authedFetch(url, fetchOptsNoCache);
    if (!res.ok) {
        throw new Error(await parseError(res));
    }
    return res.json() as Promise<PhotoCaptureStatus>;
}

/** Poll until background capture finishes (or timeout). */
export async function waitForPhotoCapture(
    baseUrl: string,
    options?: { pollMs?: number; timeoutMs?: number }
): Promise<{ ok: boolean; filename: string | null; error: string | null }> {
    const pollMs = options?.pollMs ?? 250;
    const timeoutMs = options?.timeoutMs ?? 120_000;
    const t0 = Date.now();
    for (;;) {
        const s = await fetchPhotoCaptureStatus(baseUrl);
        if (!s.busy) {
            const err = s.last_error;
            const fn = s.last_filename;
            const ok = !err && Boolean(fn);
            return { ok, filename: fn, error: err };
        }
        if (Date.now() - t0 > timeoutMs) {
            return { ok: false, filename: null, error: "Photo capture timed out" };
        }
        await new Promise((r) => setTimeout(r, pollMs));
    }
}

export type RecordingGpsMeta = {
    latitude_deg: number;
    longitude_deg: number;
    altitude_m?: number | null;
    fix?: number | null;
    satellites?: number | null;
};

export type RecordingMetaResponse = {
    filename: string;
    remark: string;
    gps?: RecordingGpsMeta | null;
};

/** GET /recordings/meta/{filename} — remark from sidecar JSON (empty string if missing). */
export async function fetchRecordingMeta(
    baseUrl: string,
    filename: string
): Promise<RecordingMetaResponse> {
    const url = joinUrl(baseUrl, `/recordings/meta/${encodeURIComponent(filename)}`);
    const res = await authedFetch(url, fetchOptsNoCache);
    if (!res.ok) {
        throw new Error(await parseError(res));
    }
    return res.json() as Promise<RecordingMetaResponse>;
}

export type RecordingMetaUpdatePayload = {
    remark: string;
    /** When set and different from current name, renames the media file and sidecar. */
    new_filename?: string | null;
};

/** PUT /recordings/meta/{filename} — write `.meta.json`; optional rename. */
export async function putRecordingMeta(
    baseUrl: string,
    filename: string,
    payload: RecordingMetaUpdatePayload
): Promise<{ ok: boolean; filename: string; remark: string }> {
    const url = joinUrl(baseUrl, `/recordings/meta/${encodeURIComponent(filename)}`);
    const body: Record<string, unknown> = { remark: payload.remark };
    if (payload.new_filename != null && payload.new_filename !== "") {
        body.new_filename = payload.new_filename;
    }
    const res = await authedFetch(url, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
        ...fetchOptsNoCache,
    });
    if (!res.ok) {
        throw new Error(await parseError(res));
    }
    return res.json() as Promise<{ ok: boolean; filename: string; remark: string }>;
}

/** DELETE /recordings/file/{name} — removes a media file and its meta sidecar. */
export async function deleteRecordingFile(baseUrl: string, filename: string): Promise<void> {
    const url = joinUrl(baseUrl, `/recordings/file/${encodeURIComponent(filename)}`);
    const res = await authedFetch(url, { method: "DELETE", ...fetchOptsNoCache });
    if (!res.ok) {
        throw new Error(await parseError(res));
    }
}
